/**
 * B73 — the ONE display-name fallback chain, shared by every surface that
 * addresses the athlete by name.
 *
 * ## The defect this exists to end
 *
 * Today greeted the founder `GOOD EVENING, RAANROAL` — the local part of their
 * email address, uppercased. Two independent copies of the chain existed
 * (`deriveSignedInUser` in `routes/today.tsx`, `deriveDisplayName` in
 * `routes/settings.tsx`) and BOTH ended with `email.split('@')[0]`. An email
 * fragment is not a name: it is an identifier the athlete never chose, it is
 * frequently unpronounceable, and printing it in a greeting reads as a bug to
 * the person being greeted. B53's PART B recommendation (rec. 1) is the rule
 * implemented here.
 *
 * ## The chain — and where it deliberately STOPS
 *
 *   1. `context_data.preferred_name` — CW-6. The explicit "what should we call
 *      you" answer wins over the formal name, matching how the coach addresses
 *      the athlete server-side (`api/lib/athlete-name.js` `resolveAthleteName`).
 *   2. `context_data.Name` — the formal name, set in Settings / vanilla
 *      onboarding.
 *   3. `user_metadata.full_name`, then `user_metadata.name` — what Google OAuth
 *      hands over automatically, and what `captureAppleName()` stashes on the
 *      one authorization where Apple returns it.
 *   4. **NOTHING.** `''`. The email local part is NOT a step, and never will be.
 *
 * ⛔ DISPLAY-ONLY. Nothing here writes. Seeding the OAuth name INTO
 * `context_data` on load would race the "never overwrite a name the athlete
 * typed" guarantee `handleSetProfileName` protects server-side (B53 PART B).
 * Show it, let the athlete's own edit persist it.
 *
 * ## Two exports because two questions
 *
 * `deriveDisplayName` answers "what is this person called?" and is allowed to
 * answer "I don't know" (`''`). A GREETING must use this one: a greeting with
 * no name is a greeting; a greeting addressed to `ATHLETE` is worse than one
 * addressed to nobody.
 *
 * `deriveDisplayLabel` answers "what do I put in this fixed-size slot?" for
 * chrome that cannot render blank (the SideNav me-block on the athlete app).
 * It falls through to a neutral generic — never to the email.
 */

/**
 * The subset of `@supabase/supabase-js`'s `User` this module reads. Kept
 * structural (rather than importing `User`) so the pure helpers stay trivially
 * testable with a two-key literal.
 */
export interface DisplayNameUser {
  user_metadata?: Record<string, unknown> | null;
  email?: string | null;
}

/**
 * The generic used ONLY by `deriveDisplayLabel`, i.e. only in athlete-app
 * chrome. Never used in a greeting, and never on a coach/guardian surface
 * (where it would be the wrong noun).
 */
export const GENERIC_DISPLAY_LABEL = 'Athlete';

/** Placeholder initial when no name is known — the `/settings` convention. */
export const NO_NAME_INITIAL = '·';

function trimmedString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

/**
 * The name the app may PRINT for the signed-in user, or `''` when it knows
 * none. Never the email local part.
 */
export function deriveDisplayName(
  user: DisplayNameUser | null | undefined,
  raw: Record<string, unknown> | null | undefined,
): string {
  const context = raw ?? {};
  const preferred = trimmedString(context.preferred_name);
  if (preferred) return preferred;
  const formal = trimmedString(context.Name);
  if (formal) return formal;
  return authMetadataName(user);
}

/**
 * The sign-in provider's name for this account (`full_name`, then `name`), or
 * `''`. Split out because the Settings Profile card seeds its `Name` ROW from
 * exactly this — the formal name specifically, never `preferred_name` (which
 * has its own row two lines below it).
 */
export function authMetadataName(user: DisplayNameUser | null | undefined): string {
  const meta = user?.user_metadata;
  if (!meta || typeof meta !== 'object') return '';
  return trimmedString((meta as Record<string, unknown>).full_name)
    || trimmedString((meta as Record<string, unknown>).name);
}

/**
 * A never-blank label for fixed chrome (the athlete SideNav me-block). Falls
 * through to a neutral generic, NOT to the email.
 */
export function deriveDisplayLabel(
  user: DisplayNameUser | null | undefined,
  raw: Record<string, unknown> | null | undefined,
): string {
  return deriveDisplayName(user, raw) || GENERIC_DISPLAY_LABEL;
}

/** Avatar initial for a display name. `·` when there is no name to initial. */
export function deriveInitial(name: string): string {
  const trimmed = name.trim();
  return trimmed ? trimmed.charAt(0).toUpperCase() : NO_NAME_INITIAL;
}
